<template>
  <article
    class="tender-card"
    role="link"
    tabindex="0"
    @click="go()"
    @keydown.enter.space.prevent="go()"
  >
    <!-- full-card clickable overlay (keeps button above it) -->
    <a
      class="tc-link-overlay"
      :href="tender.tender_url"
      aria-label="Plačiau"
    ></a>

    <div class="tc-head">
      <span
        v-if="badgeText"
        class="tc-badge"
        :class="`tc-badge--${tenderStatus}`"
      >
        {{ badgeText }}
      </span>
    </div>

    <!-- cover image -->
    <div v-if="tender.image" class="tc-tilt">
      <div class="tc-img">
        <img :src="tender.image" :alt="tender.title" loading="lazy" />
      </div>
    </div>

    <!-- content -->
    <div class="tc-info">
      <div v-if="tender.sub_title" class="tc-cat">{{ tender.sub_title }}</div>

      <h2 class="tc-title">{{ tender.title }}</h2>

      <!-- location -->
      <div v-if="tender.location_name" class="tc-location">
        <svg
          class="tc-pin"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path d="M12 22s7-5.33 7-12a7 7 0 10-14 0c0 6.67 7 12 7 12z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <span>{{ tender.location_name }}</span>
      </div>

      <p v-if="resolvedSummary" class="tc-desc" v-html="resolvedSummary"></p>

      <!-- assortments + service types -->
      <div
        v-if="
          (tender.assortments && tender.assortments.length) ||
          (tender.service_types && tender.service_types.length)
        "
        class="tc-groups"
      >
        <!-- Assortments -->
        <div
          v-if="tender.assortments && tender.assortments.length"
          class="tc-feats"
        >
          <span class="tc-label">Asortimentas:</span>
          <span
            v-for="a in tender.assortments"
            :key="`a-${a.id}`"
            class="tc-feat tc-feat-a"
            :title="a.name"
            >{{ a.name }}</span
          >
        </div>

        <!-- Service types -->
        <div
          v-if="tender.service_types && tender.service_types.length"
          class="tc-feats"
        >
          <span class="tc-label">Paslaugų rūšys:</span>
          <span
            v-for="s in tender.service_types"
            :key="`s-${s.id}`"
            class="tc-feat tc-feat-s"
            :title="s.name"
            >{{ s.name }}</span
          >
        </div>
      </div>
    </div>

    <!-- Bottom-left timer -->
    <div class="tc-meta">
      <span
        v-if="countdownText"
        class="tc-timer"
        :class="`tc-timer--${tenderStatus}`"
        v-html="countdownText"
      ></span>
    </div>

    <!-- Bottom-right CTA -->
    <div class="tc-footer">
      <a :href="tender.tender_url" class="tc-btn" @click.stop>
        <span>Plačiau</span>
        <svg
          class="tc-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  </article>
</template>

<script>
export default {
  name: 'TenderCard',
  props: { tender: { type: Object, required: true } },
  data() {
    return {
      nowTick: Date.now(),
      timerId: null,
    }
  },
  computed: {
    resolvedSummary() {
      const summary = this.tender && this.tender.summary
      if (!summary) return ''

      // If backend sends localized JSON object, resolve by current language
      if (typeof summary === 'object') {
        const lang = this.$app && this.$app.lang ? this.$app.lang : 'en'
        const value =
          summary[lang] !== undefined && summary[lang] !== null
            ? summary[lang]
            : summary.en ?? summary.lt ?? ''
        return typeof value === 'string' ? value : ''
      }

      // Fallback: plain string/HTML
      return typeof summary === 'string' ? summary : ''
    },

    dOpen() {
      // "YYYY-MM-DD" or "YYYY-MM-DD HH:mm"
      return this.parseFlexibleLocal(this.tender.submissions_open_date, {
        endOfDay: false,
      })
    },
    dClose() {
      return this.parseFlexibleLocal(this.tender.submissions_close_date, {
        endOfDay: true,
      })
    },
    dEnvelope() {
      return this.parseFlexibleLocal(this.tender.envelope_open_at, {
        endOfDay: false,
      })
    },
    tenderStatus() {
      const now = new Date(this.nowTick)
      if (this.dOpen && now < this.dOpen) return 'upcoming_open'
      if (this.dOpen && this.dClose && now >= this.dOpen && now <= this.dClose)
        return 'accepting'
      if (this.dClose && now > this.dClose) {
        if (this.dEnvelope && now < this.dEnvelope) return 'waiting_envelope'
        if (this.dEnvelope && now >= this.dEnvelope) return 'envelopes_opened'
        return 'closed'
      }
      return 'unknown'
    },
    badgeText() {
      switch (this.tenderStatus) {
        case 'upcoming_open':
          return 'Neprasidėjo'
        case 'accepting':
          return 'Vyksta prašymų priėmimas'
        case 'waiting_envelope':
          return 'Laukiama vokų atplėšimo'
        case 'envelopes_opened':
          return 'Vokai atplėšti'
        case 'closed':
          return 'Uždaryta'
        default:
          return ''
      }
    },
    countdownText() {
      const now = new Date(this.nowTick)
      if (this.tenderStatus === 'upcoming_open' && this.dOpen) {
        return `Iki prašymų priėmimo pradžios liko ${this.diffHuman(now, this.dOpen)}`
      }
      if (this.tenderStatus === 'accepting' && this.dClose) {
        return `Liko iki prašymų priėmimo pabaigos ${this.diffHuman(now, this.dClose)}`
      }
      if (this.tenderStatus === 'waiting_envelope' && this.dEnvelope) {
        return `Iki vokų atplėšimo liko ${this.diffHuman(now, this.dEnvelope)}`
      }
      return ''
    },
  },
  mounted() {
    this.timerId = setInterval(() => (this.nowTick = Date.now()), 60 * 1000)
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId)
  },
  methods: {
    go() {
      if (this.tender?.tender_url) window.location.href = this.tender.tender_url
    },
    parseFlexibleLocal(input, { endOfDay = false } = {}) {
      if (!input) return null
      const str = String(input).trim()

      // time?
      const hasTime = /\d{2}:\d{2}/.test(str)

      // divide
      const [datePart, timePart] = str.split(' ')
      const [y, m, d] = datePart.split('-').map(Number)

      if (!y || !m || !d) return null

      if (hasTime) {
        const [hh, mm] = timePart.split(':').map(Number)
        return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0)
      }

      // only days
      if (endOfDay) {
        return new Date(y, m - 1, d, 23, 59, 59, 999)
      }
      return new Date(y, m - 1, d, 0, 0, 0, 0)
    },

    // "X d. Y val." / "X val. Y min." / "X min."
    diffHuman(from, to) {
      let ms = Math.max(0, to - from)
      const totalMinutes = Math.floor(ms / 60000)
      const days = Math.floor(totalMinutes / (60 * 24))
      const hours = Math.floor((totalMinutes - days * 24 * 60) / 60)
      const minutes = totalMinutes % 60

      let parts = ''
      if (days > 0) {
        parts = `<span class="time-part time-d">${days} d.</span> <span class="time-part time-h">${hours} val.</span>`
      } else if (hours > 0) {
        parts = `<span class="time-part time-h">${hours} val.</span> <span class="time-part time-m">${minutes} min.</span>`
      } else {
        parts = `<span class="time-part time-m">${minutes} min.</span>`
      }

      // break
      return `<br>${parts}`
    },
  },
}
</script>
